import ClientPortalAPIService from '../services/api/ClientPortal/ApiService'
import MapBox from './map'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import { EventDetailsPanel } from '../components/EventDetailsPanel/EventDetailsPanel'
import User from './user'
import { SETTINGS } from '../SETTINGS'
import { debounce } from 'lodash'
import ModifyAlertsLocation from './modifyAlertsLocation'
import Sentry from './SantryIO'

export class InternationalSOSAlertsModule {
    alerts = []
    latestAlerts = []
    showAlerts = false
    currentTimeFrame = SETTINGS.FILTERS_DEFAULT_TIMEFRAME
    servicesAndCategories = {}
    userPermissions = []
    eventsSourceType = ''
    // Alert severities as specified by International SOS, for semplicity use the exact severity name as the key
    severities = SETTINGS.INTERNATIONAL_SOS_SEVERITIES
    hasFirstTimeFetchedLatestAlerts = false

    constructor() { }

    initialize(eventsSourceType) {
        this.eventsSourceType = eventsSourceType

        Promise.all([User.getLocalUserSettings(), User.getUserPermissions()]).then(values => {
            let userFilters = JSON.parse(values[0].filters)
            this.showAlerts = userFilters.enableAlerts
            this.currentTimeFrame = userFilters.timeFrame
            this.severity = userFilters.alertSeverities
            this.userPermissions = values[1]

            const todayMinusTimeFrame = moment().subtract(30, 'days')
            const todayMinusTimeFrameInUTC = moment.utc(todayMinusTimeFrame).format('YYYY-MM-DD')

            this.getAlerts(todayMinusTimeFrameInUTC).then(alerts => {
                this.latestAlerts = alerts
                this.hasFirstTimeFetchedLatestAlerts = true
                this.startLatestAlertsObserver()
            }).finally(() => {
                this.hasFirstTimeFetchedLatestAlerts = true
            })
        })
    }

    async getAlerts(startDate, maximumPeriodDate) {
        return await new Promise((resolve, reject) => {
            ClientPortalAPIService.getInternationalSOSAlerts(startDate, maximumPeriodDate).then(alerts => {
                const filteredAlerts = alerts.filter(alert => {
                    // If the user is not allowed to see medical alerts, then this seems to be the best place to do it, to no even consider them from
                    // the API, this should cascade dynamically and not show anything related to medical alerts it in the platform
                    if (alert.Service === 'Medical') {
                        if (!this.userPermissions.includes('medical') && !this.userPermissions.includes('luxury_yachting') && !this.userPermissions.includes('comprehensive_crew_support')) {
                            return false
                        } else {
                            return true
                        }
                    } else {
                        return true
                    }
                })

                this.alerts = filteredAlerts

                resolve(filteredAlerts)

                this.getCurrentAlertsAvailableCategories().then(servicesAndCategories => {
                    this.servicesAndCategories = servicesAndCategories

                    // Assure we apply the latest changes to user filters
                    User.getLocalUserSettings().then(settings => {
                        let userFilters = JSON.parse(settings.filters)

                        this.showAlerts = userFilters.enableAlerts
                        this.currentTimeFrame = userFilters.timeFrame
                        this.severity = userFilters.alertSeverities

                        this.filter(this.servicesAndCategories, this.currentTimeFrame, this.severity, maximumPeriodDate)

                        if (!this.eventsInitialized) {
                            this.addEventsListeners()
                            this.eventsInitialized = true
                        }
                    }).catch(error => { 
                        Sentry.captureException(error)
                        reject(error)
                    })
                }).catch(error => { 
                    Sentry.captureException(error)
                    reject(error)
                })
            }).catch(error => { 
                Sentry.captureException(error)
                reject(error)
            })
        })
    }

    async getModuleAlerts() {
        const alerts = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.alerts.length > 0) {
                    resolve(this.alerts)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return alerts
    }

    async getModuleLatestAlerts() {
        const latestAlerts = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.hasFirstTimeFetchedLatestAlerts) {
                    resolve(this.latestAlerts)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return latestAlerts
    }

    // Make it async, so we wait until we get the alerts data from the server
    async getCurrentAlertsAvailableCategories() {
        let userSettings = await User.getLocalUserSettings()
        const parsedUserFilters = JSON.parse(userSettings.filters)
        const userAlertFilters = parsedUserFilters.alertFilters

        const uniqueServices = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.hasFirstTimeFetchedLatestAlerts) {
                    let uniqueServices = {}

                    this.alerts.forEach(alert => {
                        if (uniqueServices[alert.Service] === undefined) {
                            uniqueServices[alert.Service] = {
                                enabled: true,
                                categories: []
                            }

                            // If user had that unique service defined, then overide the value
                            if (userAlertFilters[alert.Service] !== undefined) {
                                uniqueServices[alert.Service].enabled = userAlertFilters[alert.Service].enabled
                            }
                        }

                        alert.Categories.forEach(category => {
                            const isExsistingCategory = uniqueServices[alert.Service].categories.find(existingCategory => existingCategory.name === category)
                            if (isExsistingCategory === undefined) {
                                uniqueServices[alert.Service].categories.push({
                                    name: category,
                                    enabled: true
                                })

                                // If user had that category defined, then overide the value
                                if (userAlertFilters[alert.Service] !== undefined) {
                                    const existingUserCategoryIndex = userAlertFilters[alert.Service].categories.findIndex(existingCategory => existingCategory.name === category)

                                    if (existingUserCategoryIndex > -1) {
                                        const currentService = uniqueServices[alert.Service]
                                        currentService.categories[currentService.categories.length - 1].enabled = userAlertFilters[alert.Service].categories[existingUserCategoryIndex].enabled
                                    }
                                }
                            }
                        })
                    })

                    resolve(uniqueServices)

                    window.clearInterval(observerInterval)
                }
            }, 100)
        })

        return uniqueServices
    }

    startLatestAlertsObserver() {
        const ONE_MINUTE = 1000 * 60
        window.setInterval(() => {
            const todayMinusTimeFrame = moment().subtract(30, 'days')
            const todayMinusTimeFrameInUTC = moment.utc(todayMinusTimeFrame).format('YYYY-MM-DD')

            this.getAlerts(todayMinusTimeFrameInUTC).then(alerts => {
                this.latestAlerts = alerts
            })
        }, ONE_MINUTE)
    }

    // International SOS already returns a GEOJSON obect of the alert which is great, but unfortunataly it doesn't attach enought properties
    // like for e.g. some ID, so when it's clicked we could get more alert details, in that case we need to generate it on our side
    getAlertsFeatureCollection(alerts) {
        var featureCollection = {
            type: 'FeatureCollection',
            features: []
        }

        for (var alert of alerts) {
            var properties = {
                ['Id']: alert.Id,
                Service: alert.Service,
                isLive: moment(alert.CreatedDate ? alert.CreatedDate : alert.ActiveFrom).isSameOrAfter(moment(new Date()).subtract(24, 'hours')) ? 'Yes' : 'No',
            }

            var geometry = null

            if (alert.Locations.features.length === 1) {
                geometry = {
                    type: 'Point',
                    coordinates: alert.Locations.features[0].geometry.coordinates
                }
            } else {
                var multiPointCoordinates = []
                alert.Locations.features.forEach(featureLocation => {
                    multiPointCoordinates.push(featureLocation.geometry.coordinates)
                })

                geometry = {
                    type: 'MultiPoint',
                    coordinates: multiPointCoordinates
                }
            }

            var feature = {
                type: 'Feature',
                id: alert.Id,
                geometry: geometry,
                properties: properties
            }

            featureCollection.features.push(feature)
        }

        return featureCollection
    }

    display(alerts) {
        const alertsFeatureCollection = this.getAlertsFeatureCollection(alerts)
        const alertLayer = MapBox.map.getSource('international-sos-alerts')

        const portLayer = MapBox.map.getSource('ports')
        const airportLayer = MapBox.map.getSource('airports')
        const cityLayers = MapBox.map.getSource('cities')
        let layerType = ''
        if (this.eventsSourceType === 'maritime' && portLayer) {
            layerType = 'ports'
        } else if (airportLayer) {
            layerType = 'airports'
        } else if (cityLayers) {
            layerType = 'cities'
        }

        if (alertLayer !== undefined) {
            alertLayer.setData(alertsFeatureCollection)
        } else {
            // See Avsec Alerts module for details
            window.setTimeout(() => {
                MapBox.map.addSource('international-sos-alerts', {
                    'type': 'geojson',
                    'data': alertsFeatureCollection
                })

                MapBox.map.addLayer({
                    'id': 'layer-with-pulsing-dot',
                    'type': 'symbol',
                    'source': 'international-sos-alerts',
                    'layout': {
                        'visibility': this.showAlerts ? 'visible' : 'none',
                        'icon-image': [
                            'match',
                            ['get', 'isLive'],
                            'Yes', 'pulsing-dot',
                            ''
                        ],
                        'icon-allow-overlap': true,
                        'text-allow-overlap': true,
                        "icon-size": ['interpolate', ['linear'], ['zoom'], 3.5, 0.36, 4.5, 0.43, 7, 0.53]
                    }
                });

                MapBox.map.addLayer({
                    id: 'international-sos-alerts',
                    type: 'symbol',
                    source: 'international-sos-alerts',
                    layout: {
                        'visibility': this.showAlerts ? 'visible' : 'none',
                        'icon-image': [
                            'match',
                            ['get', 'Service'],
                            'Medical', [
                                'match',
                                ['get', 'isLive'],
                                'Yes', 'security-medical-live',
                                'medical-alert'
                            ],
                            'Security',  [
                                'match',
                                ['get', 'isLive'],
                                'Yes', 'security-alert-live',
                                'security-alert'
                            ],
                            'international-sos-alert'
                        ]
                        ,
                        'icon-allow-overlap': true,
                        'text-allow-overlap': true,
                        "icon-size": ['interpolate', ['linear'], ['zoom'], 3.5, 0.16, 4.5, 0.23, 7, 0.33]
                    }
                }, layerType)
            }, 2000)
        }

        // We need to call this function to make sure that the alerts are not overlapping
        ModifyAlertsLocation.modifyLocation()
    }

    show() {
        this.showAlerts = true
        MapBox.map.setLayoutProperty('international-sos-alerts', 'visibility', 'visible')
        MapBox.map.setLayoutProperty('layer-with-pulsing-dot', 'visibility', 'visible')
        this.debounceSavingUserSettings()
    }

    hide() {
        this.showAlerts = false
        MapBox.map.setLayoutProperty('international-sos-alerts', 'visibility', 'none')
        MapBox.map.setLayoutProperty('layer-with-pulsing-dot', 'visibility', 'none')
        this.debounceSavingUserSettings()
    }

    debounceSavingUserSettings = debounce(this.saveUserAlertsVisibilitySetting, 1500)

    saveUserAlertsVisibilitySetting() {
        User.getLocalUserSettings().then(settings => {
            let userFilters = JSON.parse(settings.filters)
            userFilters.enableAlerts = this.showAlerts

            User.updateUserFilters(userFilters)
        })
    }

    // We need a function that will display an alert on the map regardless of the filter or if they were disabled
    // E.g. user's filter don't show a specific alert, but user clicks from another module to see details of it
    showAlert(id) {
        // For simplicity we are going to assure that the alert is always not visible, so if it is not visible we
        // will display it, if it is visible then we will just have another alert on top of it, visually it should be fine
        const singleAlertData = this.getAlertDetails(id)
        if (singleAlertData !== undefined) {
            const singleAlertsFeatureCollection = this.getAlertsFeatureCollection([singleAlertData])
            const singleAlertLayer = MapBox.map.getSource('international-single-alert')
            if (singleAlertLayer !== undefined) {
                singleAlertLayer.setData(singleAlertsFeatureCollection)
            } else {
                MapBox.map.addSource('international-single-alert', {
                    'type': 'geojson',
                    'data': singleAlertsFeatureCollection
                })

                MapBox.map.addLayer({
                    id: 'international-sos-single-alert',
                    type: 'symbol',
                    source: 'international-single-alert',
                    layout: {
                        'icon-image': [
                            'match',
                            ['get', 'Service'],
                            'Medical', 'medical-alert',
                            'Security', 'security-alert',
                            'international-sos-alert'
                        ],
                        'icon-allow-overlap': true,
                        'text-allow-overlap': true,
                        "icon-size": ['interpolate', ['linear'], ['zoom'], 3.5, 0.16, 4.5, 0.23, 7, 0.33]
                    }
                })
            }

            MapBox.map.setLayoutProperty('international-sos-single-alert', 'visibility', 'visible')

            EventDetailsPanel.showInternationalSOSAlert(singleAlertData, singleAlertData.Locations.features[0].geometry.coordinates)
        }
    }

    getAlertDetails(alertID) {
        return this.alerts.find(alert => alert.Id === alertID)
    }

    addEventsListeners() {
        MapBox.map.on('mouseenter', 'international-sos-alerts', () => {
            MapBox.map.getCanvas().style.cursor = 'pointer'
        })

        MapBox.map.on('mouseleave', 'international-sos-alerts', () => {
            MapBox.map.getCanvas().style.cursor = 'inherit'
        })
    }

    filter(servicesAndCategories, timeFrame, severities, maximumPeriodDate) {
        if (timeFrame !== undefined) this.currentTimeFrame = timeFrame
        if (maximumPeriodDate === undefined) maximumPeriodDate = moment()

        this.servicesAndCategories = cloneDeep(servicesAndCategories)

        const beginningOfTheRange = moment(maximumPeriodDate).subtract(this.currentTimeFrame, 'days')
        const filteredAlerts = []

        this.alerts.forEach(alert => {
            let isAlertInEnabledSerivce = false
            let isAlertWithinSpecifiedTimeFrame = false
            let isAlertInAtLeastOneEnabledCategory = false

            for (const key in this.servicesAndCategories) {
                const service = this.servicesAndCategories[key]

                if (service.enabled && alert.Service === key) {
                    isAlertInEnabledSerivce = true
                }
            }

            // If an alert is not in an enabled parent category (service), then we don't need to do further checks
            if (isAlertInEnabledSerivce) {
                let alertDate = alert.CreatedDate !== '' ? alert.CreatedDate : alert.ActiveFrom

                if (moment(alertDate).isAfter(beginningOfTheRange)) {
                    isAlertWithinSpecifiedTimeFrame = true
                }

                if (isAlertWithinSpecifiedTimeFrame) {
                    for (let index = 0; index < servicesAndCategories[alert.Service].categories.length; index++) {
                        const filterCategory = servicesAndCategories[alert.Service].categories[index];

                        if (filterCategory.enabled) {
                            if (alert.Categories.indexOf(filterCategory.name) > -1) {
                                isAlertInAtLeastOneEnabledCategory = true
                                break
                            }
                        }
                    }

                    if (isAlertInAtLeastOneEnabledCategory) {
                        if (!!severities) {
                            if (severities[alert.Severity]) {
                                filteredAlerts.push(alert)
                            }
                        } else {
                            filteredAlerts.push(alert)
                        }
                    } else {
                        // Leave for debugging
                        // console.warn('Found an alert that is in enabled service, in the specified timeframe, but not in any enabled category', alert)
                    }
                }
            }
        })

        this.display(filteredAlerts)
    }
}

const InternationalSOSAlerts = new InternationalSOSAlertsModule()
export default InternationalSOSAlerts